import { Drawer, Form, message, Row, Spin, Typography } from 'antd';
import IssueForm from '../issueForm';
import CauseForm from '../causeForm';

import {
  Cause,
  Issue,
  QueryType,
  Routine,
  SuggestionStatus,
} from '../../types';
import { useEffect, useRef, useState } from 'react';
import SuggestionReviewDrawerHeader from '../reviewDrawerHeader';
import {
  reviewSuggestionCause,
  reviewSuggestionIssue,
  reviewSuggestionRoutine,
} from '../../utils/httpRequest';
import { useSelector } from 'react-redux';
import { selectUserId, selectUsername } from '../../slices/profile';
import RoutineForm from '../routineForm';

interface SuggestionDrawerProps {
  visible: boolean;
  onClose: (isSubmitted?: boolean) => void;
  data: Issue | Cause | Routine;
}

const ReviewRoutineDrawer = ({
  visible,
  onClose,
  data,
}: SuggestionDrawerProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [reviewResult, setReviewResult] = useState('' as SuggestionStatus);
  const [modalVisible, setModalVisible] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const userId = useSelector(selectUserId);
  const username = useSelector(selectUsername);

  const formData = useRef<Routine>(null);

  useEffect(() => {
    form.setFieldsValue(data);
  }, [form, data]);

  const handleReview = async (
    action: SuggestionStatus,
    description?: string
  ) => {
    try {
      setLoading(true);
      await form.validateFields();
      const newData = {
        ...data,
        ...formData.current,
        ...form.getFieldsValue(),
        suggestionStatus: action,
        suggestionReviewerID: userId,
        suggestionReviewerName: username,
        suggestionReviewDescription: description,
      };
      await reviewSuggestionRoutine(newData);
      form.resetFields();
      onClose(true);
    } catch (err) {
      messageApi.error('Submit failed, please try again!');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = () => {
    setReviewResult(SuggestionStatus.REJECTED);
    setModalVisible(true);
  };
  // handleReview(SuggestionStatus.REJECTED);
  const handleApprove = () => {
    setReviewResult(SuggestionStatus.APPROVED);
    setModalVisible(true);
  };

  const onConfirm = () => {
    handleReview(
      reviewResult,
      form.getFieldValue('suggestionReviewDescription')
    );
    setModalVisible(false);
  };

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      formData.current = null;
    }
  }, [visible]);

  return (
    <Drawer
      title={
        <>
          <SuggestionReviewDrawerHeader
            title="Suggest a Routine"
            name={data.name}
            onReject={handleReject}
            onApprove={handleApprove}
            reviewResult={reviewResult}
            form={form}
            onConfirm={onConfirm}
            modalVisible={modalVisible}
            onModalClose={() => {
              setModalVisible(false);
              form.resetFields();
            }}
          />
          <Typography.Text type="secondary" style={{ fontWeight: 400 }}>
            Submit By: {data?.suggestionSubmitterName || '-'} {'    '} at{' '}
            {data?.suggestionSubmittedTime?.replace('T', ' ') || '-'}
          </Typography.Text>
        </>
      }
      closable
      onClose={() => onClose()}
      open={visible}
      width={800}
    >
      {contextHolder}
      <Spin fullscreen spinning={loading}></Spin>

      <Form form={form} layout="vertical">
        <RoutineForm
          templateID={String(data.templateID)}
          form={form}
          initialRoutine={data as Routine}
          isSuggestion
          isSuggestionReview
          onChange={(routine) => (formData.current = routine)}
        />
      </Form>
    </Drawer>
  );
};

export default ReviewRoutineDrawer;
